import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { pageTitle } from '../../styles/pageStyles';
import { btnPrimary, btnSecondary } from '../../styles/buttonStyles';
import useDocumentTitle from '../../components/auth/useDocumentTitle';
import PublicationLayout from '../../components/publication-layout/PublicationLayout';
import { logError } from '../../components/utils/errors';
import api from '../../utils/api';

// ── Types ───────────────────────────────────────────────────────────────────

interface TransactionItem {
  id: number;
  title: string;
  story_id: number | null;
  story_url: string | null;
  category: string;
  campaign_names: string | null;
  completed_at: string | null;
  public_note: string | null;
  amount: number;
  amount_in_dollars: number;
  is_credit: boolean;
  contributor_name: string | null;
}

interface DebitCategory {
  category: string;
  amount_in_cents: number;
  amount_in_dollars: number;
}

interface TransactionsResponse {
  transactions: TransactionItem[];
  credit_total_in_dollars: number;
  categorized_debit_totals: DebitCategory[];
  start_date: string;
  end_date: string;
  page: number;
  total_pages: number;
  total_count: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(dollars: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(dollars);
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

function defaultStartDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultEndDate(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Styled ──────────────────────────────────────────────────────────────────

const PageTitle = styled.h1`
  ${pageTitle}
  margin: 0 0 24px 0;
`;

const FilterBar = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #666;
  }

  input {
    padding: 6px 10px;
    border: 1px solid #ccc;
    border-radius: 3px;
    font-size: 14px;
  }
`;

const SubmitButton = styled.button`
  ${btnPrimary}
  padding: 8px 20px;
  font-size: 14px;
`;

const ExportActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const DownloadLink = styled.a`
  ${btnSecondary}
  padding: 8px 20px;
  font-size: 14px;
  text-decoration: none;
`;

const EmailButton = styled.button`
  ${btnSecondary}
  padding: 8px 20px;
  font-size: 14px;
`;

const EmailNotice = styled.span<{ $isError?: boolean }>`
  font-size: 13px;
  color: ${p => (p.$isError ? '#c0392b' : '#27ae60')};
`;

const SummarySection = styled.div`
  display: flex;
  gap: 40px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SummaryCard = styled.div`
  background: #fafafa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 16px 20px;
  min-width: 200px;
`;

const SummaryLabel = styled.h5`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
`;

const CategoryItem = styled.li`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 13px;
  color: #555;
`;

const Table = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const THead = styled.div`
  display: flex;
  background: #f5f5f5;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  border-bottom: 2px solid #e0e0e0;
`;

const TRow = styled.div<{ $alt?: boolean; $isCredit?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  background: ${p => (p.$alt ? '#fafafa' : '#fff')};

  a {
    color: #0094ab;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ColTitle = styled.div`
  flex: 2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ColCategory = styled.div`flex: 1; min-width: 80px;`;
const ColCampaigns = styled.div`flex: 1; min-width: 80px;`;
const ColStoryId = styled.div`width: 80px; text-align: center;`;
const ColContributor = styled.div`flex: 1; min-width: 80px;`;
const ColDate = styled.div`width: 110px;`;
const ColNote = styled.div`flex: 1; min-width: 80px;`;
const ColAmount = styled.div<{ $isCredit?: boolean }>`
  width: 100px;
  text-align: right;
  font-weight: 600;
  color: ${p => (p.$isCredit ? '#27ae60' : '#c0392b')};
`;

const Pagination = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 16px;
  font-size: 14px;

  a {
    color: #0094ab;
    text-decoration: none;
    padding: 4px 8px;
    border-radius: 3px;
    cursor: pointer;
    flex-shrink: 0;
    white-space: nowrap;
    &:hover {
      background: #e7f5f8;
    }
  }

  span.current {
    padding: 4px 8px;
    border-radius: 3px;
    background: #0094ab;
    color: white;
    flex-shrink: 0;
    white-space: nowrap;
  }

  span.ellipsis {
    padding: 4px 4px;
    color: #999;
    flex-shrink: 0;
  }
`;

const EmptyState = styled.p`
  color: #666;
  font-size: 14px;
  padding: 20px 0;
`;

const ErrorMessage = styled.p`
  color: #c0392b;
  padding: 20px 0;
  font-size: 14px;
`;

/**
 * Compact the page range to first / current±1 / last with '...' gaps so the
 * control stays readable when a wide date range yields many pages (25/page).
 * Mirrors the established idiom in command-center/components/WidgetPagination.tsx
 * and search/SearchPage.tsx. Returns page numbers plus '...' markers.
 */
function getVisiblePages(current: number, total: number): (number | string)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | string)[] = [];
  pages.push(1);
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

// ── Component ───────────────────────────────────────────────────────────────

function FinancialTransactionsPage() {
  const { publicationId } = useParams<{ publicationId: string }>();
  useDocumentTitle('Financial transactions');

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  // Track the submitted filter params separately from input values
  const [submittedStart, setSubmittedStart] = useState(defaultStartDate);
  const [submittedEnd, setSubmittedEnd] = useState(defaultEndDate);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<TransactionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailing, setEmailing] = useState(false);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const [emailFailed, setEmailFailed] = useState(false);

  const load = useCallback(
    (sd: string, ed: string, p: number) => {
      if (!publicationId) return;
      setLoading(true);
      setError(null);

      fetch(
        `/publications/${publicationId}/transactions.json?start_date=${sd}&end_date=${ed}&page=${p}`,
        { credentials: 'same-origin', headers: { Accept: 'application/json' } }
      )
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((json: TransactionsResponse) => {
          setData(json);
          setLoading(false);
        })
        .catch(() => {
          setError('Unable to load transactions. Please refresh the page.');
          setLoading(false);
        });
    },
    [publicationId]
  );

  useEffect(() => {
    load(submittedStart, submittedEnd, page);
  }, [load, submittedStart, submittedEnd, page]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedStart(startDate);
    setSubmittedEnd(endDate);
    setPage(1);
    setEmailNotice(null);
    setEmailFailed(false);
  };

  const handleEmailReport = async () => {
    if (!publicationId) return;
    setEmailing(true);
    setEmailNotice(null);
    setEmailFailed(false);
    try {
      const res = await api.post<{ message: string }>(
        `/publications/${publicationId}/transactions/email_report.json`,
        { start_date: submittedStart, end_date: submittedEnd }
      );
      setEmailNotice(res.data.message);
    } catch (err: any) {
      logError(err instanceof Error ? err : new Error(`Transactions email report: ${err}`));
      setEmailNotice('Unable to send the report. Please try again.');
      setEmailFailed(true);
    } finally {
      setEmailing(false);
    }
  };

  if (!publicationId) return null;

  const hasTransactions = data && data.transactions.length > 0;
  const hasDebits = data && data.categorized_debit_totals.some(d => d.amount_in_cents !== 0);

  return (
    <PublicationLayout>
      <PageTitle>Financial transactions</PageTitle>

      <FilterBar onSubmit={handleSubmit}>
        <FilterGroup>
          <label htmlFor="txn-start-date">Start date</label>
          <input
            id="txn-start-date"
            type="date"
            value={startDate}
            min="2016-01-01"
            onChange={e => setStartDate(e.target.value)}
            required
          />
        </FilterGroup>
        <FilterGroup>
          <label htmlFor="txn-end-date">End date</label>
          <input
            id="txn-end-date"
            type="date"
            value={endDate}
            max={defaultEndDate()}
            onChange={e => setEndDate(e.target.value)}
            required
          />
        </FilterGroup>
        <SubmitButton type="submit">Submit</SubmitButton>
      </FilterBar>

      {loading && <p>Loading...</p>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {data && !loading && (
        <>
          <SummarySection>
            <SummaryCard>
              <SummaryLabel>Credits</SummaryLabel>
              <SummaryValue>{formatCurrency(data.credit_total_in_dollars)}</SummaryValue>
            </SummaryCard>

            {hasDebits && (
              <SummaryCard>
                <SummaryLabel>Charges</SummaryLabel>
                <CategoryList>
                  {data.categorized_debit_totals
                    .filter(d => d.amount_in_cents !== 0)
                    .map(d => (
                      <CategoryItem key={d.category}>
                        <span>{d.category}</span>
                        <span>{formatCurrency(d.amount_in_dollars)}</span>
                      </CategoryItem>
                    ))}
                </CategoryList>
              </SummaryCard>
            )}
          </SummarySection>

          {hasTransactions ? (
            <>
              <ExportActions>
                {/* target=_blank matches InvoiceDetailPage and keeps the SPA if the response isn't an attachment. */}
                <DownloadLink
                  href={`/publications/${publicationId}/transactions.csv?start_date=${submittedStart}&end_date=${submittedEnd}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download CSV"
                >
                  Download CSV
                </DownloadLink>
                <EmailButton type="button" onClick={handleEmailReport} disabled={emailing}>
                  {emailing ? 'Sending…' : 'Email report'}
                </EmailButton>
                {emailNotice && (
                  <EmailNotice $isError={emailFailed} role={emailFailed ? 'alert' : 'status'}>
                    {emailNotice}
                  </EmailNotice>
                )}
              </ExportActions>

              <Table>
                <THead>
                  <ColTitle>Charge</ColTitle>
                  <ColCategory>Category</ColCategory>
                  <ColCampaigns>Campaigns</ColCampaigns>
                  <ColStoryId>Story ID</ColStoryId>
                  <ColContributor>Contributor</ColContributor>
                  <ColDate>Date</ColDate>
                  <ColNote>Notes</ColNote>
                  <ColAmount>Amount</ColAmount>
                </THead>

                {data.transactions.map((txn, i) => (
                  <TRow key={txn.id} $alt={i % 2 === 1} $isCredit={txn.is_credit}>
                    <ColTitle>
                      {txn.story_url ? (
                        <a href={txn.story_url}>{txn.title}</a>
                      ) : (
                        txn.title
                      )}
                    </ColTitle>
                    <ColCategory>{txn.category}</ColCategory>
                    <ColCampaigns>{txn.campaign_names || ''}</ColCampaigns>
                    <ColStoryId>{txn.story_id || ''}</ColStoryId>
                    <ColContributor>{txn.contributor_name || ''}</ColContributor>
                    <ColDate>{formatDate(txn.completed_at)}</ColDate>
                    <ColNote>{txn.public_note || ''}</ColNote>
                    <ColAmount $isCredit={txn.is_credit}>
                      {txn.is_credit
                        ? formatCurrency(txn.amount_in_dollars)
                        : `-${formatCurrency(txn.amount_in_dollars)}`}
                    </ColAmount>
                  </TRow>
                ))}
              </Table>

              {data.total_pages > 1 && (
                <Pagination aria-label="Transaction pages">
                  {data.page > 1 && (
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        setPage(p => p - 1);
                      }}
                    >
                      &lsaquo; Prev
                    </a>
                  )}
                  {getVisiblePages(data.page, data.total_pages).map((p, i) =>
                    p === '...' ? (
                      <span key={`ellipsis-${i}`} className="ellipsis">
                        &hellip;
                      </span>
                    ) : p === data.page ? (
                      <span key={p} className="current">
                        {p}
                      </span>
                    ) : (
                      <a
                        key={p}
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          setPage(p as number);
                        }}
                      >
                        {p}
                      </a>
                    )
                  )}
                  {data.page < data.total_pages && (
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        setPage(p => p + 1);
                      }}
                    >
                      Next &rsaquo;
                    </a>
                  )}
                </Pagination>
              )}
            </>
          ) : (
            <EmptyState>No transactions</EmptyState>
          )}
        </>
      )}
    </PublicationLayout>
  );
}

export default FinancialTransactionsPage;
